"use client";

import React from 'react';

interface ComparisonItem {
  metric: string;
  before: string;
  after: string;
  improvement: string;
}

const comparisonData: ComparisonItem[] = [
  {
    metric: 'Inventory Turnover',
    before: '4.5× per year',
    after: '7.2× per year',
    improvement: '60% ↑'
  },
  {
    metric: 'Stockout Rate',
    before: '12-18%',
    after: '3-5%',
    improvement: '70% ↓'
  },
  {
    metric: 'Manual Reporting Hours/Week',
    before: '50 hours',
    after: '5 hours',
    improvement: '90% ↓'
  },
  {
    metric: 'Promotional ROI Visibility',
    before: 'Weeks later',
    after: 'Real-time',
    improvement: 'Instant'
  },
  {
    metric: 'Same-Store Sales Insights',
    before: 'Monthly',
    after: 'Daily',
    improvement: '30× ↑'
  },
  {
    metric: 'Markdown Losses (% revenue)',
    before: '8-10%',
    after: '4-5%',
    improvement: '₹25Cr+'
  }
];

export default function FinancialImpactSection() {
  return (
    <section className="py-14 sm:py-20 lg:py-24 bg-white px-4 sm:px-6 lg:px-8 xl:px-12 w-full md:min-h-screen md:flex md:items-center">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="font-serif text-navy text-2xl sm:text-3xl md:text-4xl xl:text-[3rem] leading-[1.2] font-bold mb-6">
            What Retail Data Fragmentation <span className="italic">Costs You</span>
          </h2>
        </div>

        {/* Before/After Comparison Table */}
        <div className="max-w-5xl mx-auto mb-12">
          <div className="bg-cream rounded-2xl p-8 md:p-10 border border-gray-200">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-navy">
                    <th className="text-left py-4 px-4 font-serif text-navy text-lg font-bold">METRIC</th>
                    <th className="text-left py-4 px-4 font-serif text-navy text-lg font-bold">BEFORE EDW</th>
                    <th className="text-left py-4 px-4 font-serif text-navy text-lg font-bold">AFTER EDW</th>
                    <th className="text-left py-4 px-4 font-serif text-navy text-lg font-bold">IMPACT</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.map((item, index) => (
                    <tr key={index} className="border-b border-gray-200 hover:bg-white/50 transition-colors">
                      <td className="py-4 px-4 text-charcoal font-medium">{item.metric}</td>
                      <td className="py-4 px-4 text-red-600">{item.before}</td>
                      <td className="py-4 px-4 text-green-600 font-medium">{item.after}</td>
                      <td className="py-4 px-4">
                        <span className="inline-block bg-gold/10 text-gold text-xs font-bold uppercase tracking-wider px-3 py-1 rounded">
                          {item.improvement}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ROI Box */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-gold to-gold/80 rounded-2xl p-8 md:p-10 shadow-xl">
            <div className="text-center mb-6">
              <h3 className="font-serif text-navy text-3xl md:text-4xl font-bold mb-4">
                ROI Example
              </h3>
              <p className="text-navy/80 text-lg leading-[1.6] mb-6">
                For a 100-store retail chain with ₹400 crore annual revenue:
              </p>
            </div>
            <div className="space-y-3 mb-6">
              <div className="bg-white/90 rounded-xl p-4">
                <p className="text-charcoal text-base"><span className="font-bold text-navy">Reducing markdowns</span> from 10% to 5% = <span className="text-gold font-bold">₹20 crore annual savings</span></p>
              </div>
              <div className="bg-white/90 rounded-xl p-4">
                <p className="text-charcoal text-base"><span className="font-bold text-navy">Improving inventory turnover</span> = <span className="text-gold font-bold">₹30 crore working capital freed up</span></p>
              </div>
              <div className="bg-white/90 rounded-xl p-4">
                <p className="text-charcoal text-base"><span className="font-bold text-navy">Time saved on reporting</span> = <span className="text-gold font-bold">45 hours/week = 6 FTE equivalent</span></p>
              </div>
            </div>
            <p className="text-navy/70 text-sm text-center font-medium">
              Typical EDW payback period: <span className="text-navy font-bold">10-14 months</span>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}